/**
 * 系统模块配置
 * 统一管理系统模块的路由、菜单、权限配置
 * @author AI Assistant
 * @date 2024-07-08
 */

import type { ModuleConfig } from '@/types/module-config'

/**
 * 系统模块配置
 */
const moduleConfig: ModuleConfig = {
  id: 'system',
  name: '系统管理',
  description: '系统管理模块，包含用户、角色、菜单等管理功能',
  version: '1.0.0',
  enabled: true,

  // 路由配置
  routes: [
    {
      path: '/system',
      name: 'System',
      component: '@/layouts/index.vue',
      redirect: '/system/user',
      meta: {
        title: '系统管理',
        icon: 'Setting',
        roles: ['admin', 'super_admin'],
        alwaysShow: true,
        order: 100
      },
      children: [
        {
          path: 'user',
          name: 'SystemUser',
          component: '@/modules/system/pages/user.vue',
          meta: {
            title: '用户管理',
            icon: 'User',
            roles: ['admin', 'super_admin'],
            permissions: ['system:user:view'],
            keepAlive: true
          }
        },

        {
          path: 'role',
          name: 'SystemRole',
          component: '@/modules/system/pages/role.vue',
          meta: {
            title: '角色管理',
            icon: 'UserFilled',
            roles: ['admin', 'super_admin'],
            permissions: ['system:role:view'],
            keepAlive: true
          }
        },
        {
          path: 'menus',
          name: 'SystemMenu',
          component: '@/modules/system/pages/menus.vue',
          meta: {
            title: '菜单管理',
            icon: 'Menu',
            roles: ['admin', 'super_admin'],
            permissions: ['system:menus:view'],
            keepAlive: true
          }
        }
      ]
    }
  ],

  // 权限配置
  permissions: [
    {
      code: 'system:user:view',
      name: '查看用户',
      type: 'menu',
      description: '查看用户列表',
      apiPath: '/users'
    },
    {
      code: 'system:user:add',
      name: '添加用户',
      type: 'button',
      description: '添加新用户',
      apiPath: '/users'
    },
    {
      code: 'system:user:edit',
      name: '编辑用户',
      type: 'button',
      description: '编辑用户信息',
      apiPath: '/users/:id'
    },
    {
      code: 'system:user:delete',
      name: '删除用户',
      type: 'button',
      description: '删除用户',
      apiPath: '/users/:id'
    },
    {
      code: 'system:user:detail',
      name: '查看用户详情',
      type: 'operation',
      description: '查看用户详细信息',
      apiPath: '/users/:id'
    },
    {
      code: 'system:user:status',
      name: '修改用户状态',
      type: 'operation',
      description: '启用/禁用用户',
      apiPath: '/users/:id/status'
    },
    {
      code: 'system:user:reset-password',
      name: '重置密码',
      type: 'operation',
      description: '重置用户密码',
      apiPath: '/users/:id/reset-password'
    },
    {
      code: 'system:user:assign-role',
      name: '分配角色',
      type: 'operation',
      description: '为用户分配角色',
      apiPath: '/users/:id/roles'
    },
    {
      code: 'system:role:view',
      name: '查看角色',
      type: 'menu',
      description: '查看角色列表',
      apiPath: '/roles'
    },
    {
      code: 'system:role:add',
      name: '添加角色',
      type: 'button',
      description: '添加新角色',
      apiPath: '/roles'
    },
    {
      code: 'system:role:edit',
      name: '编辑角色',
      type: 'button',
      description: '编辑角色信息',
      apiPath: '/roles/:id'
    },
    {
      code: 'system:role:delete',
      name: '删除角色',
      type: 'button',
      description: '删除角色',
      apiPath: '/roles/:id'
    },
    {
      code: 'system:role:detail',
      name: '查看角色详情',
      type: 'operation',
      description: '查看角色详细信息',
      apiPath: '/roles/:id'
    },
    {
      code: 'system:role:assign-permission',
      name: '分配权限',
      type: 'operation',
      description: '为角色分配权限',
      apiPath: '/roles/:id/permissions'
    },
    {
      code: 'system:role:assign-user',
      name: '分配用户',
      type: 'operation',
      description: '为角色分配用户',
      apiPath: '/roles/:id/users'
    },
    {
      code: 'system:menus:view',
      name: '查看菜单',
      type: 'menu',
      description: '查看菜单列表',
      apiPath: '/menus'
    },
    {
      code: 'system:menu:add',
      name: '添加菜单',
      type: 'button',
      description: '添加新菜单',
      apiPath: '/menus'
    },
    {
      code: 'system:menu:edit',
      name: '编辑菜单',
      type: 'button',
      description: '编辑菜单信息',
      apiPath: '/menus/:id'
    },
    {
      code: 'system:menu:delete',
      name: '删除菜单',
      type: 'button',
      description: '删除菜单',
      apiPath: '/admin/permission/delete'
    },
    {
      code: 'system:menu:detail',
      name: '查看菜单详情',
      type: 'operation',
      description: '查看菜单详细信息',
      apiPath: '/menus/:id'
    },
    {
      code: 'system:menu:sort',
      name: '菜单排序',
      type: 'operation',
      description: '调整菜单排序',
      apiPath: '/menus/sort'
    },
    {
      code: 'system:menu:status',
      name: '修改菜单状态',
      type: 'operation',
      description: '启用/禁用菜单',
      apiPath: '/menus/:id/status'
    },
    {
      code: 'system:permission:view',
      name: '查看权限',
      type: 'menu',
      description: '查看权限列表',
      apiPath: '/permissions'
    },
    {
      code: 'system:permission:sync',
      name: '同步权限',
      type: 'operation',
      description: '同步系统权限',
      apiPath: '/permissions/sync'
    }
  ],

  // 导航配置
  navigation: {
    name: 'system',
    title: '系统管理',
    icon: 'Setting',
    permissions: ['system:view'],
    order: 100,
    items: [
      {
        id: 'system-users',
        title: '用户管理',
        path: '/system/user',
        icon: 'User',
        order: 1,
        permissions: ['system:user:view'],
        module: 'system',
        component: '@/modules/system/pages/user.vue'
      },
      {
        id: 'system-roles',
        title: '角色管理',
        path: '/system/role',
        icon: 'UserFilled',
        order: 2,
        permissions: ['system:role:view'],
        module: 'system',
        component: '@/modules/system/pages/role.vue'
      },
      {
        id: 'system-menus',
        title: '菜单管理',
        path: '/system/menus',
        icon: 'Menu',
        order: 3,
        permissions: ['system:menus:view'],
        module: 'system',
        component: '@/modules/system/pages/menus.vue'
      }
    ]
  }
}

export default moduleConfig
